// Shared cancel-session hook used by both building-capture-screen and
// site-capture-screen. Wraps the open/submit/error state machine for
// the CancelSessionDialog so the page files don't each duplicate the
// boilerplate (and so they stay under the lint:max-lines cap).
//
// The hook does NOT trigger navigation on success — callers decide
// where to land after cancel (different per scope). It returns the
// authoritative result so the caller can pass the new building status
// to its own state if needed.

import { useCallback, useState } from 'react'

import type { BuildingStatus } from '@citycare/shared'

import { ApiError } from '../../hooks/api'
import { db as defaultDb } from '../../hooks/powersync/client'

import type { CaptureClient } from '../client/capture-client.types'

/**
 * Minimal surface needed to evict the cancelled session from the
 * local PowerSync mirror. Defined locally (instead of importing
 * `AbstractPowerSyncDatabase`) so:
 *   1. The capture/pages module stays out of `@powersync/*` direct
 *      imports (architecture-boundary lint Rule 4 — only `hooks/`
 *      may import @powersync/*; the production singleton at
 *      `hooks/powersync/client` IS in `hooks/` and is the only
 *      direct dependency.)
 *   2. Tests can pass an in-memory fake without dragging the real
 *      PowerSync runtime into the unit-test bundle.
 */
export interface CancelSessionLocalDb {
  execute(sql: string, params?: unknown[]): Promise<unknown>
}

export interface UseCancelSessionInput {
  captureClient: Pick<CaptureClient, 'dispatch'>
  sessionId: string | null
  /** Caller's last-known sync_version for LWW guard. */
  syncVersion: number
  /**
   * Local PowerSync DB used to evict the cancelled session from the
   * client mirror after the REST DELETE succeeds. Defaults to the
   * shared singleton; tests inject a recording fake.
   */
  localDb?: CancelSessionLocalDb
}

export interface UseCancelSessionResult {
  isOpen: boolean
  isSubmitting: boolean
  error: string | null
  open: () => void
  close: () => void
  /**
   * Confirm and dispatch the cancel mutation. Resolves with the
   * authoritative result on success, or null on failure (the error
   * message is exposed via `.error`). Callers handle navigation
   * separately based on scope.
   */
  confirm: () => Promise<{
    sessionId: string
    buildingId: string | null
    buildingStatus: BuildingStatus | null
  } | null>
}

function describeCancelError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

/**
 * Evict the cancelled session (and its captured items) from the
 * local PowerSync mirror so the next render of `useActiveSession`
 * returns null immediately, instead of waiting for the `captures`
 * sync stream to deliver the server-side delete.
 *
 * Without this, a field user who taps Cancel and immediately
 * navigates back to the building/site capture screen finds the
 * cancelled session still in the local read cache and is offered
 * the chance to "resume" it (Codex P2 on PR #469).
 *
 * Cleanup failure is non-blocking: the server delete already
 * succeeded, so the next sync tick will still bring the local
 * mirror in line. We surface the error via the warn channel so
 * the regression is visible if it turns into a pattern.
 */
async function evictLocalSession(
  db: CancelSessionLocalDb,
  sessionId: string,
): Promise<void> {
  try {
    // capture_items column is `capture_session_id` (see
    // hooks/powersync/schema.ts:270 — "historical bug fixed in sync
    // rules"). Using `session_id` here throws "no such column" inside
    // the try/catch, swallowing both deletes and leaving the local
    // mirror exposing the stale active session until the next sync
    // tick — the exact resume-cancelled-session regression this hook
    // exists to prevent. Codex P2 on PR #509.
    await db.execute(
      'DELETE FROM capture_items WHERE capture_session_id = ?',
      [sessionId],
    )
    await db.execute('DELETE FROM capture_sessions WHERE id = ?', [sessionId])
  } catch (cleanupErr) {
    // Diagnostic only — same console.warn pattern used by
    // hooks/powersync/provider.tsx for non-fatal sync hiccups.
    console.warn(
      'useCancelSession: local PowerSync eviction failed',
      cleanupErr,
    )
  }
}

export function useCancelSession({
  captureClient,
  sessionId,
  syncVersion,
  localDb = defaultDb,
}: UseCancelSessionInput): UseCancelSessionResult {
  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const open = useCallback(() => {
    setError(null)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    if (isSubmitting) return
    setIsOpen(false)
    setError(null)
  }, [isSubmitting])

  const confirm = useCallback(async () => {
    if (sessionId === null) return null
    setIsSubmitting(true)
    setError(null)
    try {
      const result = await captureClient.dispatch({
        kind: 'cancelSession',
        sessionId,
        syncVersion,
      })
      if (result.kind !== 'cancelSession') {
        // Adapter returned a result for a different mutation — defensive
        // guard so we don't silently mis-report success.
        throw new Error('Unexpected cancel result kind')
      }
      // Server delete OK → evict local mirror BEFORE flipping isOpen
      // to false, so the caller's post-confirm navigation finds an
      // empty active-session result instead of the stale local row.
      await evictLocalSession(localDb, result.sessionId)
      setIsSubmitting(false)
      setIsOpen(false)
      return {
        sessionId: result.sessionId,
        buildingId: result.buildingId,
        buildingStatus: result.buildingStatus,
      }
    } catch (err) {
      setIsSubmitting(false)
      setError(describeCancelError(err))
      return null
    }
  }, [captureClient, sessionId, syncVersion, localDb])

  return { isOpen, isSubmitting, error, open, close, confirm }
}
